import React from "react";

/**
 * SafeLink — render an <a> with a scheme-whitelisted href.
 *
 * Prevents javascript:/data:/vbscript: payloads smuggled through
 * user-controlled URLs (DR credential document_url, BDC supporting docs,
 * admin-edited content). Defaults open a new tab with noopener+noreferrer.
 *
 * Unsafe URLs render as plain text with a "[unsafe URL blocked]" suffix
 * so AD still sees what was submitted — useful for a reject-with-notes
 * decision. Pass fallback={<span>…</span>} to customize the blocked render.
 *
 * Review item H8.
 */

const SAFE_SCHEMES = ["http:", "https:", "mailto:"];

// Unicode colon lookalikes that some URL parsers normalize to ':' before
// the scheme check runs — javascript：alert(1) would slip through otherwise.
// U+FF1A fullwidth colon, U+A789 modifier letter colon.
const UNICODE_COLON_LOOKALIKE = /[\uFF1A\uA789]/;

export function isSafeUrl(raw) {
  if (!raw) return false;
  const s = String(raw).trim();
  if (!s) return false;
  // Reject non-ASCII colon lookalikes up front.
  if (UNICODE_COLON_LOOKALIKE.test(s)) return false;
  // Parse via the URL constructor; tolerate relative paths by giving a base.
  try {
    // Relative paths (starting with / or without scheme) are considered safe
    // for in-app links. Reject protocol-relative (//host) because it will
    // resolve to the current protocol, not what the AD is expecting.
    if (s.startsWith("//")) return false;
    if (s.startsWith("/")) return true;
    const u = new URL(s);
    return SAFE_SCHEMES.includes((u.protocol || "").toLowerCase());
  } catch {
    return false;
  }
}

function SafeLink({
  href,
  children,
  className,
  target = "_blank",
  rel = "noopener noreferrer",
  fallback = null,
  ...rest
}) {
  if (!isSafeUrl(href)) {
    return (
      fallback || (
        <span className={className} title="blocked: unsafe URL scheme">
          {children || href} <em>[unsafe URL blocked]</em>
        </span>
      )
    );
  }
  return (
    <a href={href} className={className} target={target} rel={rel} {...rest}>
      {children || href}
    </a>
  );
}

export default SafeLink;
